import React, { useCallback, useEffect, useMemo, useState } from "react";
import Map, { Marker, NavigationControl } from "react-map-gl";
import "mapbox-gl/dist/mapbox-gl.css";
import { toast } from "sonner";
import {
  MapPinned, MapPin, RefreshCw, LocateFixed, Crosshair, AlertTriangle, Building2, CheckCircle2, Navigation,
} from "lucide-react";
import PageHeader from "@/components/layout/PageHeader";
import { Button } from "@/components/ui/button";
import { api, formatApiError } from "@/lib/api";
import { DEFAULT_CENTER } from "@/pages/fleet/constants";
import { useMapboxToken } from "@/hooks/useMapboxToken";

const STATUS_META = {
  missing: { label: "Missing location", color: "#B91C1C", bg: "#FEE2E2" },
  outside_ksa: { label: "Outside KSA", color: "#9A3412", bg: "#FFEDD5" },
  city_mismatch: { label: "Far from city", color: "#B45309", bg: "#FEF3C7" },
};

export default function LocationWorklist() {
  const MAPBOX_TOKEN = useMapboxToken();
  const [items, setItems] = useState([]);
  const [counts, setCounts] = useState({});
  const [loading, setLoading] = useState(true);
  const [sel, setSel] = useState(null);
  const [pin, setPin] = useState(null);          // { latitude, longitude }
  const [saving, setSaving] = useState(false);
  const [view, setView] = useState({ longitude: DEFAULT_CENTER?.longitude || 46.6753, latitude: DEFAULT_CENTER?.latitude || 24.7136, zoom: 5 });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/fleet/location-worklist");
      setItems(r.data.items || []); setCounts(r.data.counts || {});
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  function select(it) {
    setSel(it);
    const start = it.latitude != null ? { latitude: it.latitude, longitude: it.longitude }
      : it.city_centroid ? { latitude: it.city_centroid.latitude, longitude: it.city_centroid.longitude } : null;
    setPin(start);
    setView({ longitude: (start?.longitude) || DEFAULT_CENTER?.longitude || 46.6753,
              latitude: (start?.latitude) || DEFAULT_CENTER?.latitude || 24.7136, zoom: start ? 12 : 5 });
  }

  async function relocate(mode, coords) {
    if (!sel) return;
    if (mode === "manual" && (!coords || coords.latitude == null)) { toast.error("Click the map to drop a pin first"); return; }
    setSaving(true);
    try {
      const body = mode === "geocode" ? { mode } : { mode, latitude: coords.latitude, longitude: coords.longitude };
      await api.post(`/fleet/patients/${sel.patient_id}/relocate`, body);
      toast.success(`Location updated for ${sel.patient_name}`);
      setItems((arr) => arr.filter((x) => x.patient_id !== sel.patient_id));
      setSel(null); setPin(null);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  }

  const totalActive = useMemo(() => items.filter((i) => i.has_active_visit).length, [items]);

  return (
    <div data-testid="location-worklist-page">
      <PageHeader
        title="Location Worklist"
        subtitle="Patients with missing, out-of-country or off-city map pins. Fix them so routes and ETAs reflect real distances."
        actions={<Button variant="outline" onClick={load} className="border-[#E5E7EB]" data-testid="lw-refresh"><RefreshCw className={`w-4 h-4 mr-1.5 ${loading ? "animate-spin" : ""}`} /> Refresh</Button>}
      />

      <div className="flex flex-wrap gap-2 mb-3">
        {Object.entries(STATUS_META).map(([k, m]) => (
          <span key={k} className="text-[12px] px-2.5 py-1 rounded-lg font-medium" style={{ background: m.bg, color: m.color }} data-testid={`lw-count-${k}`}>
            {m.label}: {counts[k] || 0}
          </span>
        ))}
        {totalActive > 0 && <span className="text-[12px] px-2.5 py-1 rounded-lg font-medium bg-[#0B7E8C14] text-[#0B7E8C]">{totalActive} with active visits</span>}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-5 gap-3">
        {/* Worklist */}
        <div className="lg:col-span-2 hah-card p-3 max-h-[640px] overflow-auto" data-testid="lw-list">
          {loading ? (
            <div className="p-8 text-center text-sm text-[#6B7280]">Loading…</div>
          ) : items.length === 0 ? (
            <div className="p-10 text-center" data-testid="lw-empty">
              <CheckCircle2 className="w-8 h-8 text-[#10B981] mx-auto mb-3" />
              <p className="text-sm text-[#6B7280]">All patient locations look good.</p>
            </div>
          ) : (
            <div className="space-y-2">
              {items.map((it) => {
                const m = STATUS_META[it.status] || STATUS_META.missing;
                return (
                  <button key={it.patient_id} onClick={() => select(it)} data-testid={`lw-item-${it.patient_id}`}
                    className={`w-full text-left p-3 rounded-lg border transition-colors ${sel?.patient_id === it.patient_id ? "border-[#0B7E8C] bg-[#0B7E8C08]" : "border-[#F1F5F9] hover:bg-[#F8FAFC]"}`}>
                    <div className="flex items-center justify-between gap-2">
                      <div className="min-w-0">
                        <div className="text-[14px] font-medium text-[#1F2937] truncate">{it.patient_name}</div>
                        <div className="text-[11px] text-[#94A3B8] font-mono">{it.mrn}{it.city ? ` · ${it.city}` : ""}</div>
                      </div>
                      <span className="text-[10px] px-1.5 py-0.5 rounded-full font-medium shrink-0" style={{ background: m.bg, color: m.color }}>{m.label}</span>
                    </div>
                    <div className="flex items-center gap-2 mt-1 text-[11px] text-[#6B7280]">
                      {it.distance_km != null && <span>{it.distance_km} km off</span>}
                      {it.has_active_visit && <span className="inline-flex items-center gap-1 text-[#0B7E8C]"><Navigation className="w-3 h-3" /> active visit</span>}
                    </div>
                  </button>
                );
              })}
            </div>
          )}
        </div>

        {/* Map + actions */}
        <div className="lg:col-span-3 hah-card p-3">
          {!sel ? (
            <div className="h-[560px] flex flex-col items-center justify-center text-center text-[#94A3B8]">
              <MapPinned className="w-10 h-10 mb-3 text-[#CBD5E1]" />
              <p className="text-sm">Select a patient to re-pin their location on the map.</p>
            </div>
          ) : (
            <>
              <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
                <div>
                  <div className="text-[14px] font-semibold text-[#1F2937]">{sel.patient_name} <span className="text-[12px] text-[#94A3B8] font-normal">· {sel.mrn}</span></div>
                  <div className="text-[11px] text-[#6B7280]">Click the map or drag the pin to set the exact home location{sel.city ? ` in ${sel.city}` : ""}.</div>
                </div>
                <div className="flex items-center gap-2">
                  <Button size="sm" variant="outline" onClick={() => relocate("geocode")} disabled={saving} className="border-[#E5E7EB]" data-testid="lw-geocode">
                    <LocateFixed className="w-3.5 h-3.5 mr-1" /> Re-geocode address
                  </Button>
                  {sel.city_centroid && (
                    <Button size="sm" variant="outline" disabled={saving} className="border-[#E5E7EB]" data-testid="lw-city-center"
                      onClick={() => relocate("city_center", { latitude: sel.city_centroid.latitude, longitude: sel.city_centroid.longitude })}>
                      <Building2 className="w-3.5 h-3.5 mr-1" /> Use {sel.city} center
                    </Button>
                  )}
                </div>
              </div>

              <div className="rounded-lg overflow-hidden" style={{ height: 480 }}>
                {MAPBOX_TOKEN ? (
                  <Map {...view} onMove={(e) => setView(e.viewState)} mapboxAccessToken={MAPBOX_TOKEN}
                       mapStyle="mapbox://styles/mapbox/streets-v12" style={{ width: "100%", height: "100%" }}
                       onClick={(e) => setPin({ latitude: e.lngLat.lat, longitude: e.lngLat.lng })}>
                    <NavigationControl position="top-right" />
                    {pin && (
                      <Marker longitude={pin.longitude} latitude={pin.latitude} anchor="bottom" draggable
                              onDragEnd={(e) => setPin({ latitude: e.lngLat.lat, longitude: e.lngLat.lng })}>
                        <MapPin className="w-8 h-8 text-[#0B7E8C]" fill="#0B7E8C" fillOpacity={0.25} />
                      </Marker>
                    )}
                  </Map>
                ) : (
                  <div className="h-full flex items-center justify-center text-sm text-[#94A3B8]">Map unavailable — MAPBOX_TOKEN not configured.</div>
                )}
              </div>

              <div className="flex items-center justify-between mt-2">
                <div className="text-[12px] text-[#6B7280] inline-flex items-center gap-1.5">
                  <Crosshair className="w-3.5 h-3.5" />
                  {pin ? `${pin.latitude.toFixed(5)}, ${pin.longitude.toFixed(5)}` : "No pin placed"}
                </div>
                <Button onClick={() => relocate("manual", pin)} disabled={saving || !pin} className="bg-[#0B7E8C] hover:bg-[#128C97] text-white" data-testid="lw-save">
                  <CheckCircle2 className="w-4 h-4 mr-1.5" /> {saving ? "Saving…" : "Save location"}
                </Button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
